import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Calendar, Gift } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const BetaBanner = () => {
  const [betaStatus, setBetaStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if banner was dismissed this session
    const wasDismissed = sessionStorage.getItem('beta_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    // Fetch beta status
    const fetchBetaStatus = async () => {
      try {
        const res = await axios.get(`${API}/api/beta/status`);
        setBetaStatus(res.data);
      } catch (e) {
        console.error('Error fetching beta status:', e);
      }
    };

    fetchBetaStatus();
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('beta_banner_dismissed', 'true');
    setDismissed(true);
  };

  if (!betaStatus || !betaStatus.is_beta_mode || !betaStatus.show_banner || dismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="sticky top-0 left-0 right-0 z-40 bg-gradient-to-r from-violet-600 to-rose-500 text-white px-4 py-3 shadow-lg"
        data-testid="beta-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-bold">BETA</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-medium">
                ¡Estás disfrutando de Vynco 100% gratis!
              </span>
              <span className="ml-2 text-white/80">
                Hasta el 1 de enero 2027
              </span>
            </div>
            <div className="sm:hidden text-sm">
              Beta gratis hasta 01/06/2026
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-sm bg-white/10 rounded-full px-3 py-1">
              <Calendar className="w-4 h-4" />
              <span>{betaStatus.days_remaining} días restantes</span>
            </div>
            
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Cerrar banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Compact banner for smaller spaces
export const BetaBadge = ({ className = "" }) => {
  const [isBeta, setIsBeta] = useState(false);

  useEffect(() => {
    const checkBeta = async () => {
      try {
        const res = await axios.get(`${API}/api/beta/status`);
        setIsBeta(res.data.is_beta_mode);
      } catch (e) {
        console.error('Error checking beta:', e);
      }
    };
    checkBeta();
  }, []);

  if (!isBeta) return null;

  return (
    <div className={`inline-flex items-center gap-1 bg-gradient-to-r from-violet-600 to-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full ${className}`}>
      <Gift className="w-3 h-3" />
      BETA GRATIS
    </div>
  );
};

export default BetaBanner;
